/**
 * Inspection Checklist Panel Component
 * 
 * Main checklist UI for technicians running inspections
 */

import { useState, useCallback } from 'react';
import { Loader2, Send, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTicketInspection } from '@/hooks/useTicketInspection';
import { useInspectionTemplate, type GroupedTemplateItems } from '@/hooks/useInspectionTemplate';
import { useInspectionExecution, type UpsertItemParams } from '@/hooks/useInspectionExecution';
import { InspectionItemRow } from './InspectionItemRow';
import { DashPhotoCapture } from './DashPhotoCapture';
import { SuggestChangeModal } from './SuggestChangeModal';
import { useOptiDvi } from '@/hooks/useOptiDvi';
import { cn } from '@/lib/utils';

interface InspectionChecklistPanelProps {
  ticketId: string;
  shopId: string;
  currentMileage?: number | null;
  vehicle?: {
    year?: number;
    make?: string;
    model?: string;
  };
}

function SystemSection({
  group,
  inspectionId,
  shopId,
  ticketId,
  currentMileage,
  resultsByItemId,
  evidenceByItemId,
  isEditable,
  onUpsertItem,
  onEvidenceChange,
}: {
  group: GroupedTemplateItems;
  inspectionId: string;
  shopId: string;
  ticketId: string;
  currentMileage?: number | null;
  resultsByItemId: Map<string, any>;
  evidenceByItemId: Map<string, any[]>;
  isEditable: boolean;
  onUpsertItem: (params: UpsertItemParams) => Promise<void>;
  onEvidenceChange: () => void;
}) {
  const [isOpen, setIsOpen] = useState(true);
  
  // Count completed items
  const completedCount = group.items.filter(
    item => resultsByItemId.get(item.id)?.severity != null
  ).length;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full p-3 bg-muted/50 hover:bg-muted rounded-lg transition-colors"
        >
          <div className="flex items-center gap-2">
            {isOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <span className="font-medium">{group.system.name}</span>
          </div>
          <span className="text-sm text-muted-foreground">
            {completedCount} / {group.items.length}
          </span>
        </button>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="space-y-2 mt-2">
        {group.items.map((item) => {
          const result = resultsByItemId.get(item.id);
          const itemId = result?.id;
          const evidence = itemId ? (evidenceByItemId.get(itemId) || []) : [];

          // Check if this is a dash photo item
          if (item.shared_key === 'DASH_PHOTO') {
            return (
              <DashPhotoCapture
                key={item.id}
                shopId={shopId}
                ticketId={ticketId}
                inspectionItemId={itemId}
                templateItemId={item.id}
                systemKey={item.system_key}
                itemKey={item.item_key}
                evidence={evidence}
                currentMileage={currentMileage}
                isEditable={isEditable}
                onUpdate={async (params) => {
                  await onUpsertItem({
                    ...params,
                    templateItemId: item.id,
                    systemKey: item.system_key,
                    itemKey: item.item_key,
                    sharedKey: item.shared_key,
                  });
                }}
                onEvidenceChange={onEvidenceChange}
              />
            );
          }

          return (
            <InspectionItemRow
              key={item.id}
              templateItem={item}
              result={result}
              evidence={evidence}
              shopId={shopId}
              ticketId={ticketId}
              inspectionItemId={itemId}
              isEditable={isEditable}
              onUpdate={async (params) => {
                await onUpsertItem({
                  ...params,
                  templateItemId: item.id,
                  systemKey: item.system_key,
                  itemKey: item.item_key,
                  sharedKey: item.shared_key,
                });
              }}
              onEvidenceChange={onEvidenceChange}
            />
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function InspectionChecklistPanel({
  ticketId,
  shopId,
  currentMileage,
  vehicle,
}: InspectionChecklistPanelProps) {
  const { isEnabled, canUpload } = useOptiDvi();
  
  // Get or create inspection
  const {
    inspection,
    isLoading: inspectionLoading,
    hasInspection,
    createInspection,
    isCreating,
  } = useTicketInspection(ticketId, shopId, 'DVI');

  // Load template structure
  const {
    groupedItems,
    items: templateItems,
    isLoading: templateLoading,
  } = useInspectionTemplate(inspection?.template_id);

  // Load execution data
  const {
    resultsByItemId,
    evidenceByItemId,
    isEditable,
    upsertItemResult,
    submitInspection,
    isSubmitting,
    refetch: refetchExecution,
  } = useInspectionExecution(
    inspection?.id,
    inspection?.shop_id,
    inspection?.status
  );

  const handleStartInspection = useCallback(async () => {
    await createInspection();
  }, [createInspection]);

  const handleSubmit = useCallback(async () => {
    const result = await submitInspection();
    if (!result.success && result.error === 'missing_evidence') {
      // Error toast is shown by the hook
    }
  }, [submitInspection]);

  // Feature gate check
  if (!isEnabled) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p>OptiDVI is not enabled for this shop.</p>
      </div>
    );
  }

  // Loading state
  if (inspectionLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // No inspection yet - show start button
  if (!hasInspection) {
    return (
      <div className="p-6 text-center space-y-4">
        <div className="text-muted-foreground">
          <p className="font-medium">No inspection started</p>
          <p className="text-sm">Start a new inspection for this vehicle.</p>
        </div>
        <Button 
          onClick={handleStartInspection} 
          disabled={isCreating || !canUpload}
        >
          {isCreating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Starting...
            </>
          ) : (
            'Start Inspection'
          )}
        </Button>
      </div>
    );
  }

  // Template loading
  if (templateLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading checklist...</span>
      </div>
    );
  }

  // Submitted/locked state
  const isLocked = inspection?.status !== 'IN_PROGRESS';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">Inspection Checklist</h3>
          <p className="text-sm text-muted-foreground">
            {inspection?.status === 'SUBMITTED' ? 'Submitted for review' : 
             inspection?.status === 'APPROVED' ? 'Approved' :
             inspection?.status === 'REJECTED' ? 'Rejected' :
             'Complete all required items'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SuggestChangeModal
            shopId={shopId}
            templateKey="DVI"
            templateItems={templateItems}
          />
          {isEditable && (
            <Button 
              onClick={handleSubmit} 
              disabled={isSubmitting}
              className="gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Submit Inspection
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Locked status alert */}
      {isLocked && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This inspection has been {inspection?.status?.toLowerCase()}. 
            {inspection?.status === 'SUBMITTED' && ' Waiting for manager review.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Checklist sections */}
      <div className="space-y-3">
        {groupedItems.map((group) => (
          <SystemSection
            key={group.system.system_key}
            group={group}
            inspectionId={inspection!.id}
            shopId={inspection!.shop_id}
            ticketId={ticketId}
            currentMileage={currentMileage}
            resultsByItemId={resultsByItemId}
            evidenceByItemId={evidenceByItemId}
            isEditable={isEditable}
            onUpsertItem={upsertItemResult}
            onEvidenceChange={refetchExecution}
          />
        ))}
      </div>

      {/* Empty state */}
      {groupedItems.length === 0 && !templateLoading && (
        <div className="text-center py-8 text-muted-foreground">
          <p>No inspection items configured.</p>
          <p className="text-sm">Contact your manager to set up the inspection template.</p>
        </div>
      )}
    </div>
  );
}
